import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, UserPlus } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Select } from "@/components/ui/Select";
import {
  checkCreditorDocument,
  createCreditor,
  fetchCreditorCityOptions,
  lookupCreditorCnpj,
  type CreditorCityOption,
  type CreditorOption,
} from "@/services/solicitacaoPagamento";
import { formatCpfCnpjInput, onlyDigits, validateBrDocument, validateCnpj } from "@/utils/brDocument";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (creditor: CreditorOption) => void;
}

interface FormState {
  name: string;
  registerNumber: string;
  tradeName: string;
  stateRegistrationType: "C" | "I" | "N";
  stateRegistrationNumber: string;
  cityRegistrationNumber: string;
  phoneDdd: string;
  phoneNumber: string;
  email: string;
  zipCode: string;
  streetName: string;
  number: string;
  complement: string;
  neighborhood: string;
  cityId: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  registerNumber: "",
  tradeName: "",
  stateRegistrationType: "N",
  stateRegistrationNumber: "",
  cityRegistrationNumber: "",
  phoneDdd: "",
  phoneNumber: "",
  email: "",
  zipCode: "",
  streetName: "",
  number: "",
  complement: "",
  neighborhood: "",
  cityId: "",
};

const IE_TYPE_OPTIONS = [
  { value: "C", label: "Contribuinte ICMS" },
  { value: "I", label: "Isento de IE" },
  { value: "N", label: "Não contribuinte" },
];

function formatZipInput(raw: string): string {
  const d = onlyDigits(raw).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

export function CadastrarFornecedorModal({ open, onClose, onCreated }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState | "submit", string>>>({});
  const [checkingDoc, setCheckingDoc] = useState(false);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [existingCreditor, setExistingCreditor] = useState<CreditorOption | null>(null);
  const [cities, setCities] = useState<CreditorCityOption[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const reset = useCallback(() => {
    setForm(EMPTY_FORM);
    setErrors({});
    setExistingCreditor(null);
    setCheckingDoc(false);
    setLoadingCep(false);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCities(true);
    fetchCreditorCityOptions()
      .then((opts) => {
        if (!cancelled) setCities(opts);
      })
      .finally(() => {
        if (!cancelled) setLoadingCities(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: c.id, label: c.label })),
    [cities],
  );

  const patch = (partial: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(partial) as Array<keyof FormState>) {
        delete next[key];
      }
      delete next.submit;
      return next;
    });
    if ("registerNumber" in partial) setExistingCreditor(null);
  };

  const matchCityId = (cityName: string, state: string): string => {
    const nameNorm = normalizeText(cityName);
    const stateNorm = state.trim().toUpperCase();
    const matches = cities.filter(
      (c) => normalizeText(c.name) === nameNorm && c.state.toUpperCase() === stateNorm,
    );
    return matches.length === 1 ? matches[0].id : "";
  };

  const isCnpj = onlyDigits(form.registerNumber).length === 14;

  const runDocumentCheck = async (registerNumber: string) => {
    const validation = validateBrDocument(registerNumber);
    if (!validation.valid) return;

    setCheckingDoc(true);
    try {
      const result = await checkCreditorDocument(registerNumber);
      if (result.exists && result.creditor) {
        setExistingCreditor(result.creditor);
      } else {
        setExistingCreditor(null);
      }
    } catch {
      /* Falha na consulta não bloqueia o cadastro. */
    } finally {
      setCheckingDoc(false);
    }
  };

  const runCnpjLookup = async (registerNumber: string) => {
    const digits = onlyDigits(registerNumber);
    if (digits.length !== 14 || !validateCnpj(digits)) return;

    setLoadingCnpj(true);
    try {
      const data = await lookupCreditorCnpj(registerNumber);
      if (!data) return;

      const cityId = matchCityId(data.municipio, data.uf);
      patch({
        name: data.razaoSocial || form.name,
        tradeName: data.nomeFantasia || form.tradeName,
        stateRegistrationType: data.stateRegistrationType,
        stateRegistrationNumber: data.stateRegistrationNumber,
        cityRegistrationNumber: data.cityRegistrationNumber || form.cityRegistrationNumber,
        zipCode: data.cep ? formatZipInput(data.cep) : form.zipCode,
        streetName: data.logradouro || form.streetName,
        number: data.numero || form.number,
        complement: data.complemento || form.complement,
        neighborhood: data.bairro || form.neighborhood,
        ...(cityId ? { cityId } : {}),
      });
    } catch {
      /* Consulta opcional — não bloqueia cadastro manual. */
    } finally {
      setLoadingCnpj(false);
    }
  };

  const handleDocumentBlur = () => {
    const digits = onlyDigits(form.registerNumber);
    if (digits.length === 11 || digits.length === 14) {
      void runDocumentCheck(form.registerNumber);
    }
    if (digits.length === 14) {
      void runCnpjLookup(form.registerNumber);
    }
  };

  const handleCepBlur = async () => {
    const cep = onlyDigits(form.zipCode);
    if (cep.length !== 8) return;

    setLoadingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = (await res.json()) as {
        erro?: boolean;
        logradouro?: string;
        bairro?: string;
        localidade?: string;
        uf?: string;
      };
      if (data.erro) return;

      const cityId = matchCityId(data.localidade ?? "", data.uf ?? "");
      patch({
        streetName: data.logradouro ?? form.streetName,
        neighborhood: data.bairro ?? form.neighborhood,
        ...(cityId ? { cityId } : {}),
      });
    } catch {
      /* CEP lookup opcional. */
    } finally {
      setLoadingCep(false);
    }
  };

  const validateForm = (): boolean => {
    const next: Partial<Record<keyof FormState | "submit", string>> = {};
    if (!form.name.trim()) next.name = "Informe o nome do fornecedor.";
    const docValidation = validateBrDocument(form.registerNumber);
    if (!docValidation.valid) next.registerNumber = docValidation.error;

    const ddd = onlyDigits(form.phoneDdd);
    const phone = onlyDigits(form.phoneNumber);
    if (ddd.length !== 2) next.phoneDdd = "Informe o DDD.";
    if (phone.length < 8) next.phoneNumber = "Informe o telefone.";

    if (onlyDigits(form.zipCode).length !== 8) next.zipCode = "Informe um CEP válido.";
    if (!form.streetName.trim()) next.streetName = "Informe o logradouro.";
    if (!form.number.trim()) next.number = "Informe o número.";
    if (!form.neighborhood.trim()) next.neighborhood = "Informe o bairro.";
    if (!form.cityId) next.cityId = "Selecione o município.";

    if (isCnpj) {
      if (form.stateRegistrationType === "C" && !onlyDigits(form.stateRegistrationNumber)) {
        next.stateRegistrationNumber = "Informe a inscrição estadual.";
      }
    }

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = "E-mail inválido.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleUseExisting = () => {
    if (!existingCreditor) return;
    onCreated(existingCreditor);
    onClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (existingCreditor) {
      setErrors({ submit: "Este CPF/CNPJ já está cadastrado. Use o fornecedor existente." });
      return;
    }

    setSubmitting(true);
    setErrors({});
    try {
      const creditor = await createCreditor({
        name: form.name.trim(),
        registerNumber: form.registerNumber,
        phoneDdd: form.phoneDdd,
        phoneNumber: form.phoneNumber,
        email: form.email.trim(),
        tradeName: form.tradeName.trim() || undefined,
        stateRegistrationType: isCnpj ? form.stateRegistrationType : undefined,
        stateRegistrationNumber:
          isCnpj && form.stateRegistrationType === "C"
            ? onlyDigits(form.stateRegistrationNumber)
            : undefined,
        cityRegistrationNumber: isCnpj ? onlyDigits(form.cityRegistrationNumber) || undefined : undefined,
        address: {
          cityId: form.cityId,
          streetName: form.streetName.trim(),
          number: form.number.trim(),
          neighborhood: form.neighborhood.trim(),
          zipCode: form.zipCode,
          complement: form.complement.trim(),
        },
      });
      onCreated(creditor);
      onClose();
    } catch (e: unknown) {
      const resp = e as {
        response?: { status?: number; data?: { error?: string; creditor?: CreditorOption } };
      };
      if (resp.response?.status === 409 && resp.response.data?.creditor) {
        setExistingCreditor(resp.response.data.creditor);
        setErrors({
          submit: resp.response.data.error ?? "Este CPF/CNPJ já está cadastrado no Sienge.",
        });
      } else {
        setErrors({
          submit:
            resp.response?.data?.error ??
            (e instanceof Error ? e.message : "Não foi possível cadastrar o fornecedor."),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cadastrar fornecedor no Sienge"
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSubmit()} loading={submitting} leftIcon={<UserPlus className="w-4 h-4" />}>
            Cadastrar fornecedor
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          O Sienge exige endereço completo e dados fiscais para CNPJ (inscrição estadual, nome fantasia).
          Ao informar o CNPJ, buscamos automaticamente os dados na Receita Federal quando possível.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nome / Razão social *"
            value={form.name}
            onChange={(e) => patch({ name: e.target.value })}
            error={errors.name}
            placeholder="Ex.: Djair Construções"
          />
          <Input
            label="CPF / CNPJ *"
            value={form.registerNumber}
            onChange={(e) => patch({ registerNumber: formatCpfCnpjInput(e.target.value) })}
            onBlur={handleDocumentBlur}
            error={errors.registerNumber}
            placeholder="000.000.000-00 ou 00.000.000/0000-00"
            hint={
              checkingDoc
                ? "Verificando cadastro..."
                : loadingCnpj
                  ? "Consultando Receita Federal..."
                  : undefined
            }
          />
        </div>

        {isCnpj && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Dados fiscais (CNPJ)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome fantasia"
                value={form.tradeName}
                onChange={(e) => patch({ tradeName: e.target.value })}
                placeholder="Nome comercial"
              />
              <Input
                label="Inscrição municipal"
                value={form.cityRegistrationNumber}
                onChange={(e) => patch({ cityRegistrationNumber: onlyDigits(e.target.value) })}
                placeholder="Opcional"
                inputMode="numeric"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Inscrição estadual *"
                value={form.stateRegistrationType}
                onChange={(e) =>
                  patch({
                    stateRegistrationType: e.target.value as "C" | "I" | "N",
                    ...(e.target.value !== "C" ? { stateRegistrationNumber: "" } : {}),
                  })
                }
                options={IE_TYPE_OPTIONS}
              />
              {form.stateRegistrationType === "C" && (
                <Input
                  label="Número da IE *"
                  value={form.stateRegistrationNumber}
                  onChange={(e) => patch({ stateRegistrationNumber: e.target.value })}
                  error={errors.stateRegistrationNumber}
                  placeholder="Somente números"
                />
              )}
              {form.stateRegistrationType !== "C" && (
                <p className="text-xs text-slate-500 md:col-span-2">
                  Para &quot;Isento&quot; ou &quot;Não contribuinte&quot;, o Sienge exige o texto{" "}
                  <strong>ISENTO</strong> no campo de IE — isso é enviado automaticamente ao cadastrar.
                </p>
              )}
            </div>
          </div>
        )}

        {existingCreditor && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Fornecedor já cadastrado</p>
                <p className="mt-1">
                  {existingCreditor.name}
                  {existingCreditor.documentNumber ? ` (${existingCreditor.documentNumber})` : ""}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={handleUseExisting}
                >
                  Usar este fornecedor
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Endereço *</p>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
            <Input
              label="CEP *"
              value={form.zipCode}
              onChange={(e) => patch({ zipCode: formatZipInput(e.target.value) })}
              onBlur={() => void handleCepBlur()}
              error={errors.zipCode}
              placeholder="00000-000"
              inputMode="numeric"
              hint={loadingCep ? "Buscando CEP..." : "Preenche logradouro e bairro automaticamente"}
            />
            <SearchableSelect
              label="Município *"
              options={cityOptions}
              value={form.cityId}
              onChange={(cityId) => patch({ cityId })}
              loading={loadingCities}
              error={errors.cityId}
              placeholder="Selecione o município..."
              dropdownMinWidth={360}
            />
          </div>

          <Input
            label="Logradouro *"
            value={form.streetName}
            onChange={(e) => patch({ streetName: e.target.value })}
            error={errors.streetName}
            placeholder="Rua, avenida, estrada..."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Número *"
              value={form.number}
              onChange={(e) => patch({ number: e.target.value })}
              error={errors.number}
              placeholder="123"
            />
            <Input
              label="Complemento"
              value={form.complement}
              onChange={(e) => patch({ complement: e.target.value })}
              placeholder="Sala, bloco, apto..."
            />
            <Input
              label="Bairro *"
              value={form.neighborhood}
              onChange={(e) => patch({ neighborhood: e.target.value })}
              error={errors.neighborhood}
              placeholder="Centro"
            />
          </div>
        </div>

        <div className="grid grid-cols-[88px_1fr] gap-3">
          <Input
            label="DDD *"
            value={form.phoneDdd}
            onChange={(e) => patch({ phoneDdd: onlyDigits(e.target.value).slice(0, 2) })}
            error={errors.phoneDdd}
            placeholder="48"
            inputMode="numeric"
          />
          <Input
            label="Telefone *"
            value={form.phoneNumber}
            onChange={(e) => patch({ phoneNumber: onlyDigits(e.target.value).slice(0, 11) })}
            error={errors.phoneNumber}
            placeholder="999998888"
            inputMode="tel"
          />
        </div>

        <Input
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => patch({ email: e.target.value })}
          error={errors.email}
          placeholder="contato@fornecedor.com.br"
        />

        {errors.submit && (
          <p className="text-sm text-red-600 font-medium">{errors.submit}</p>
        )}
      </div>
    </Modal>
  );
}
